import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Calendar, Clock, Phone, Mail } from 'lucide-react';
import { getFullDayName } from '../../lib/utils';

/* BBA brand tokens */
const BBA_BLUE   = '#0057A8';
const BBA_ORANGE = '#FF6B00';
const BBA_DARK   = '#1A1A2E';

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export default function FiltersCard({
  locations, selectedLocation, onLocationChange,
  selectedDate, setSelectedDate, minDate, maxDate,
  isLocationClosed, getOperatingHoursDisplay, getBreakInfo,
}) {
  const selectedDayName = getFullDayName(selectedDate);

  return (
    <div
      className="p-4 sm:p-6 mb-8 rounded-2xl"
      style={{
        background: '#FFFFFF',
        border: `2px solid ${BBA_BLUE}`,
        boxShadow: '0 4px 16px rgba(0, 87, 168, 0.15)',
      }}
    >
      {/* Location + Date: stacked on mobile, side-by-side on sm+ */}
      <div className="filters-card-row">

        {/* Location */}
        <div className="flex-1">
          <Label
            className="uppercase mb-2 block tracking-wide"
            style={{ color: BBA_BLUE, fontWeight: 700, fontSize: '13px', letterSpacing: '0.8px' }}
          >
            <MapPin className="w-4 h-4 inline mr-1" /> Location
          </Label>
          <Select value={selectedLocation?.id} onValueChange={onLocationChange}>
            <SelectTrigger
              data-testid="location-select"
              className="w-full rounded-lg"
              style={{
                border: `1.5px solid ${BBA_BLUE}`,
                background: '#F8FAFF',
                color: BBA_DARK,
                fontSize: '15px',
                height: '48px',
                padding: '0 12px',
              }}
            >
              <SelectValue placeholder="Select a location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedLocation && (
            <p className="mt-1" style={{ color: '#333333', fontSize: '13px' }}>
              {selectedLocation.address}
            </p>
          )}
        </div>

        {/* Date */}
        <div className="flex-1">
          <Label
            htmlFor="date"
            className="uppercase mb-2 block tracking-wide"
            style={{ color: BBA_BLUE, fontWeight: 700, fontSize: '13px', letterSpacing: '0.8px' }}
            data-testid="date-label"
          >
            <Calendar className="w-4 h-4 inline mr-1" /> Date
            {selectedDayName && <span data-testid="date-day-of-week">: {selectedDayName}</span>}
          </Label>
          <Input
            id="date"
            data-testid="date-input"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            min={minDate}
            max={maxDate}
            className="w-full rounded-lg"
            style={{
              border: `1.5px solid ${BBA_BLUE}`,
              background: '#F8FAFF',
              color: BBA_DARK,
              fontSize: '15px',
              height: '48px',
              padding: '0 12px',
            }}
          />
          <p className="mt-1" style={{ color: '#333333', fontSize: '13px' }}>
            Bookings up to 1 month in advance
          </p>
        </div>
      </div>

      {/* Location info panel — shown when a location is selected */}
      {selectedLocation && (
        <div
          className="mt-4 p-4 rounded-xl"
          style={{ background: '#F0F7FF', border: `1px solid ${BBA_BLUE}22` }}
        >
          {/* Operating hours */}
          {selectedDate && (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Clock className="w-4 h-4 flex-shrink-0" style={{ color: BBA_BLUE }} />
              <span className="text-sm font-medium" style={{ color: BBA_DARK }}>Operating Hours:</span>
              <span
                className="text-sm"
                style={{
                  fontWeight: 600,
                  color: isLocationClosed ? '#DC2626' : BBA_BLUE,
                }}
              >
                {getOperatingHoursDisplay()}
              </span>
            </div>
          )}

          {/* Phone + Email + Facebook */}
          <div className="location-meta-row gap-y-2">
            {selectedLocation.phone && (
              <a
                href={`tel:${selectedLocation.phone}`}
                className="flex items-center gap-1.5 hover:underline"
                style={{ color: BBA_ORANGE, fontWeight: 600, fontSize: '14px' }}
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                {selectedLocation.phone}
              </a>
            )}
            {selectedLocation.email && (
              <a
                href={`mailto:${selectedLocation.email}`}
                className="flex items-center gap-1.5 hover:underline truncate"
                style={{ color: BBA_ORANGE, fontWeight: 600, fontSize: '14px' }}
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                {selectedLocation.email}
              </a>
            )}
            <a
              href="https://www.facebook.com/brubballassociation/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:underline"
              style={{ color: '#1877F2', fontWeight: 600, fontSize: '14px' }}
              data-testid="facebook-link"
            >
              <FacebookIcon />
              Follow us on Facebook
            </a>
          </div>

          {/* Break / blocked hours */}
          {getBreakInfo && getBreakInfo.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-bold text-amber-800">Blocked Hours</p>
              {getBreakInfo.map((b, idx) => (
                <p key={`${b.start}-${b.end}-${idx}`} className="text-sm text-amber-700 mt-1">
                  {b.start} – {b.end}: {b.reason || 'Break'}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

